import { useEffect } from "react";

export interface SeoProps {
  title: string;
  description: string;
  keywords: string;
  path: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function buildOriginPath(path: string): string {
  const base = __BASE_PATH__ || "/";
  const cleanBase = base.replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${cleanBase}${cleanPath}`;
}

export default function Seo({
  title,
  description,
  keywords,
  path,
  image = "https://readdy.ai/api/search-image?query=warm%20amber%20abstract%20editorial%20background%20with%20soft%20light%20and%20muted%20green%20accents%2C%20cinematic%20minimal%20composition&width=1200&height=630&seq=guatian-og-default&orientation=landscape",
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const url = buildOriginPath(path);
    document.title = title;

    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    setMeta("name", "robots", "index,follow");
    setMeta("name", "author", "看料吃瓜社");
    setMeta("name", "last-modified", new Date().toISOString());

    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "看料吃瓜社");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("property", "og:locale", "zh_CN");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    setCanonical(url);

    const scriptId = "readdy-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.remove();
    }
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, image, jsonLd]);

  return null;
}